import { useEffect, useState } from 'react'
import TableRow from '@mui/material/TableRow'
import AdvancedTable from 'components/AdvancedTable'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Language } from 'types/Language'
import RequestStatus from 'components/RequestStatus'
import { axiosDelete } from 'connectors/axiosDelete'
import { axiosPost } from 'connectors/axiosPost'
import { useAppContext } from 'context/AppContext/AppContext'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useAlert } from 'context/AlertContext'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import { renderCells } from 'utils/renderCells'
import { LabelBox } from 'components/label-box'
import { EditCell } from 'components/ActionDropdown/ActionDropdown'
import ConfirmDialog from 'components/ConfirmDialog'
import { TrashIcon } from 'assets/icons/trash'
import { TrendUp } from 'assets/icons/trend-up'
import PageLoader from 'components/PageLoader'

const MarketLanguages = (): JSX.Element => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { changeMessage } = useAlert()
  const { markets } = useAppContext()
  const { token, refreshToken } = useAuthContext()
  const [itemToDelete, setItemToDelete] = useState<number | null>(null)
  const { mutate: chooseDefault, isLoading } = useMutation(
    async ({ id, marketId }: { id: number; marketId: number }) =>
      await axiosPost<Language[]>(token, refreshToken, 'market-languages/change-default', {
        id: id.toString(),
        marketId: marketId?.toString(),
      }),
    {
      onSuccess: (response, id) => {
        if (response !== undefined && 'data' in response) {
          void queryClient.refetchQueries(['markets-languages-query'])
        }
      },
    }
  )

  const {
    mutate,
    data: deleteResponse,
    reset,
    isLoading: isDeleting,
  } = useMutation(async (id: number) => await axiosDelete(token, refreshToken, `market-languages/${id}`), {
    onSuccess: (response, id) => {
      if (response !== undefined && 'data' in response) {
        void queryClient.refetchQueries(['markets-languages-query'])
      }
    },
  })

  useEffect(() => {
    if (deleteResponse !== undefined && 'error' in deleteResponse) {
      changeMessage(deleteResponse?.error?.data?.message, 'error', reset)
    }
  }, [deleteResponse])

  if (markets === undefined) {
    return <RequestStatus data={markets} />
  }

  return (
    <>
      <Typography variant="h5" component="h1" fontWeight={600} mb={2}>
        {t('navigation.marketLanguages')}
      </Typography>
      <AdvancedTable<Language>
        name="markets-languages"
        endpoint="market-languages"
        headings={[
          { type: 'empty', name: 'ID', field: 'id', sort: true, width: 90 },
          { type: 'text', name: t('common.name'), field: 'name', sort: true },
          { type: 'text', name: t('common.code'), field: 'code', sort: true, width: 120 },
          { type: 'options', name: t('singleField.market'), field: 'marketId', sort: true, width: 150 },
          { type: 'empty', name: t('common.default'), field: 'default', sort: true, width: 140 },
          { type: 'empty', name: '', field: '', width: 90 },
        ]}
        options={{
          marketId: markets.data
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(el => ({
              id: el.id.toString(),
              value: (
                <Box>
                  {el.name}

                  {!!el.description && (
                    <Typography display="block" color="grey.400" variant="caption" lineHeight={1} fontWeight={400}>
                      {el.description}
                    </Typography>
                  )}
                </Box>
              ),
            })),
        }}
        renderRow={(row, idx) => (
          <TableRow key={idx}>
            {renderCells([
              { label: 'ID', value: <LabelBox first={`#${row.id}`} /> },
              { label: t('common.name'), value: <LabelBox first={row.name} /> },
              { label: t('common.code'), value: <LabelBox second={row.code.toUpperCase()} /> },
              { label: t('singleField.market'), value: <LabelBox second={row.market.name} /> },
              { label: t('common.default'), value: row.default ? t('common.default') : '' },
            ])}
            <EditCell>
              <Box display="flex" gap={0.25}>
                <Tooltip title={t('common.setDefault')}>
                  <IconButton
                    onClick={() => {
                      chooseDefault({ id: row.id, marketId: row.marketId })
                    }}
                  >
                    <TrendUp />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.delete')}>
                  <IconButton
                    onClick={() => {
                      chooseDefault({ id: row.id, marketId: row.marketId })
                    }}
                  >
                    <TrashIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </EditCell>
          </TableRow>
        )}
      />
      {itemToDelete !== null ? (
        <ConfirmDialog
          open={itemToDelete !== null}
          onAgree={() => {
            mutate(itemToDelete)
          }}
          onDisagree={() => {
            setItemToDelete(null)
          }}
          question={t('dialogs.delete')}
        />
      ) : null}
      {(isLoading || isDeleting) && <PageLoader />}
    </>
  )
}

export default MarketLanguages
